import React, { useEffect, useId, useRef, useState } from "react";
import Modal from "./Modal";
import { btn } from "../utils/buttonStyles";
import { useApi } from "../hooks/useApi";
import { Actions, Errors, Field, UserSelect, inputCls, useSubmit } from "./NercAssetForms";
import { formatDateOnly, saveBlob, todayIso } from "../utils/nercAssets";
import {
  OUTCOME_LABELS, STATEMENT_REASON_LABELS, emptyPatchRow, patchRowProblems, rowFromCve, rowToRequest, timeZoneOptions,
} from "../utils/nercPatches";

/**
 * GRC → Assets → NERC sites → Patch management: the forms (point 4). Each checks what it can before
 * sending and always shows the server's message when it refuses. Dates are calendar dates in the
 * organisation's NERC time zone; "today" is the server's (the overview's settings), never the browser's.
 */

const noteCls = "text-xs text-gray-600";
const warnCls = "text-xs text-orange-800 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2";

// ── Settings (P14) ───────────────────────────────────────────────────────────────────────────

export function SettingsForm({ settings, onClose, onSaved }) {
  const apiCall = useApi();
  const [timeZoneId, setTimeZoneId] = useState(settings?.timeZoneSet ? settings.timeZoneId : "");
  const { saving, errors, submit } = useSubmit(onSaved);
  const zones = timeZoneOptions();
  const onSubmit = (e) => {
    e.preventDefault();
    submit(() => apiCall("PUT", "/nerc-patches/settings", { timeZoneId }), timeZoneId ? [] : ["Choose the organisation's NERC time zone."]);
  };
  return (
    <Modal isOpen onClose={onClose} title="Patch management settings">
      <form onSubmit={onSubmit} className="space-y-3" data-testid="nerc-patch-settings-form">
        <Field label="NERC time zone" hint="Every 35-day clock counts calendar days in this time zone (day 35 is on time, day 36 is late).">
          <select className={inputCls} value={timeZoneId} onChange={(e) => setTimeZoneId(e.target.value)} data-testid="nerc-patch-time-zone">
            <option value="">— Choose —</option>
            {zones.map((z) => <option key={z} value={z}>{z}</option>)}
          </select>
        </Field>
        <Field label="Assessed against">
          <input className={inputCls} value={settings?.cip007Version || "CIP-007-6"} readOnly disabled />
        </Field>
        <p className={noteCls}>CIP-007-7.1 becomes selectable once Shared Cyber Infrastructure can be recorded in the NERC inventory.</p>
        <Errors items={errors} />
        <Actions saving={saving} onCancel={onClose} />
      </form>
    </Modal>
  );
}

// ── Audit export (P12) ───────────────────────────────────────────────────────────────────────

const minusDays = (iso, days) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d - days)).toISOString().slice(0, 10);
};

/** The CIP-007 R2 export: events dated in the period and the records as recorded at its end, as CSV or PDF. */
export function ExportForm({ sites, today, onClose }) {
  const apiCall = useApi();
  const day = today || todayIso();
  const [from, setFrom] = useState(minusDays(day, 90));
  const [to, setTo] = useState(day);
  const [siteId, setSiteId] = useState("");
  const [format, setFormat] = useState("pdf");
  const { saving, errors, submit } = useSubmit(onClose);
  const onSubmit = (e) => {
    e.preventDefault();
    const local = [];
    if (!from || !to) local.push("Choose the period's first and last day.");
    else if (from > to) local.push("The period must start before it ends.");
    else if (to > day) local.push("The period cannot end in the future.");
    submit(async () => {
      const query = new URLSearchParams({ from, to, format });
      if (siteId) query.set("siteId", siteId);
      const blob = await apiCall("GET", `/nerc-patches/export?${query}`, null, false, "blob");
      if (blob) saveBlob(blob, `nerc-cip007-r2-${from.replaceAll("-", "")}-${to.replaceAll("-", "")}.${format}`);
    }, local);
  };
  return (
    <Modal isOpen onClose={onClose} title="Export CIP-007 R2 evidence">
      <form onSubmit={onSubmit} className="space-y-3" data-testid="nerc-patch-export-form">
        <p className={noteCls}>
          The export lists what happened during the period (evaluations, decisions, plan changes and approvals, breaches, reminders, activity)
          and the records as they stood at the end of its last day: scope, sources, patches and each asset&apos;s state, plans with every version,
          and the CIP-007 R2.1–R2.4 status with what was missed.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="From"><input type="date" className={inputCls} value={from} max={day} onChange={(e) => setFrom(e.target.value)} data-testid="nerc-patch-export-from" /></Field>
          <Field label="To"><input type="date" className={inputCls} value={to} max={day} onChange={(e) => setTo(e.target.value)} data-testid="nerc-patch-export-to" /></Field>
        </div>
        <Field label="Site">
          <select className={inputCls} value={siteId} onChange={(e) => setSiteId(e.target.value)} data-testid="nerc-patch-export-site">
            <option value="">All sites</option>
            {(sites || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
        <Field label="Format">
          <select className={inputCls} value={format} onChange={(e) => setFormat(e.target.value)} data-testid="nerc-patch-export-format">
            <option value="pdf">PDF (for auditors)</option>
            <option value="csv">CSV (spreadsheet)</option>
          </select>
        </Field>
        <Errors items={errors} />
        <Actions saving={saving} onCancel={onClose} label="Download" />
      </form>
    </Modal>
  );
}

// ── Patch source (P2) ────────────────────────────────────────────────────────────────────────

export function SourceForm({ source, assets, users, onClose, onSaved }) {
  const apiCall = useApi();
  const [form, setForm] = useState({
    name: source?.name || "", vendor: source?.vendor || "", product: source?.product || "",
    location: source?.location || "", notes: source?.notes || "", ownerUserId: source?.ownerUserId ?? null,
    cyberAssetIds: source?.cyberAssetIds || [],
  });
  const { saving, errors, submit } = useSubmit(onSaved);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleAsset = (id) => set("cyberAssetIds")(form.cyberAssetIds.includes(id) ? form.cyberAssetIds.filter((a) => a !== id) : [...form.cyberAssetIds, id]);

  const onSubmit = (e) => {
    e.preventDefault();
    submit(() => source
      ? apiCall("PUT", `/nerc-patches/sources/${source.id}`, form)
      : apiCall("POST", "/nerc-patches/sources", form),
    form.name.trim() ? [] : ["A source name is required."]);
  };

  return (
    <Modal isOpen onClose={onClose} title={source ? "Edit patch source" : "Add patch source"} size="2xl">
      <form onSubmit={onSubmit} className="space-y-3" data-testid="nerc-patch-source-form">
        <Field label="Name" hint="Where the patches come from, e.g. &quot;SEL RTAC firmware&quot;.">
          <input className={inputCls} value={form.name} onChange={(e) => set("name")(e.target.value)} maxLength={200} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Vendor"><VendorInput value={form.vendor} onChange={set("vendor")} /></Field>
          <Field label="Product"><input className={inputCls} value={form.product} onChange={(e) => set("product")(e.target.value)} maxLength={200} /></Field>
        </div>
        <Field label="Where patches are published" hint="A URL or a description (vendor portal, mailing list).">
          <input className={inputCls} value={form.location} onChange={(e) => set("location")(e.target.value)} maxLength={1000} />
        </Field>
        <UserSelect users={users} value={form.ownerUserId} onChange={set("ownerUserId")} label="Owner (evaluates the source and is reminded)" testId="nerc-patch-source-owner" />
        <fieldset>
          <legend className="block text-xs font-medium text-gray-700 mb-1">Cyber Assets this source covers</legend>
          <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
            {assets.length === 0 && <p className="text-xs text-gray-500">No Cyber Assets in the inventory yet.</p>}
            {assets.map((a) => (
              <label key={a.id} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.cyberAssetIds.includes(a.id)} onChange={() => toggleAsset(a.id)} />
                <span>{a.name} <span className="text-xs text-gray-500">{a.siteName} · {a.classification}{a.inScope ? "" : " · not in CIP-007 scope"}</span></span>
              </label>
            ))}
          </div>
        </fieldset>
        <Field label="Notes"><textarea className={inputCls} rows={2} value={form.notes} onChange={(e) => set("notes")(e.target.value)} maxLength={2000} /></Field>
        <Errors items={errors} />
        <Actions saving={saving} onCancel={onClose} />
      </form>
    </Modal>
  );
}

// ── "No patch source" statement (P2) ─────────────────────────────────────────────────────────

export function StatementForm({ asset, onClose, onSaved }) {
  const apiCall = useApi();
  const [reasonType, setReasonType] = useState("NoSourceExists");
  const [reason, setReason] = useState("");
  const { saving, errors, submit } = useSubmit(onSaved);
  const onSubmit = (e) => {
    e.preventDefault();
    submit(() => apiCall("POST", "/nerc-patches/no-source-statements", { cyberAssetId: asset.id, reason, reasonType }),
      reason.trim() ? [] : [reasonType === "NotUpdateable" ? "Say why the asset cannot be updated." : "Say why the asset has no patch source."]);
  };
  return (
    <Modal isOpen onClose={onClose} title={`No patch source — ${asset.name}`}>
      <form onSubmit={onSubmit} className="space-y-3" data-testid="nerc-patch-statement-form">
        <p className={noteCls}>CIP-007 R2.1 covers updateable Cyber Assets for which a patch source exists. Record why this asset has none.</p>
        <Field label="Statement">
          <select className={inputCls} value={reasonType} onChange={(e) => setReasonType(e.target.value)} data-testid="nerc-patch-statement-type">
            {Object.entries(STATEMENT_REASON_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
        <Field label="Reason"><textarea className={inputCls} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} /></Field>
        <Errors items={errors} />
        <Actions saving={saving} onCancel={onClose} label="Record statement" />
      </form>
    </Modal>
  );
}

// ── Vendor names (P4): the organisation's existing spellings, suggested as the user types ────

/** One request per page: every vendor field on it shares the list. */
let vendorRequest = null;

function useVendorNames() {
  const apiCall = useApi();
  const [names, setNames] = useState([]);
  useEffect(() => {
    let live = true;
    vendorRequest = vendorRequest || apiCall("GET", "/nerc-patches/vendors").catch(() => []);
    vendorRequest.then((list) => { if (live) setNames(Array.isArray(list) ? list : []); });
    return () => { live = false; };
  }, [apiCall]);
  return names;
}

/** Clears the shared vendor list (after a save, or when the page reloads for another organisation). */
export const forgetVendorNames = () => { vendorRequest = null; };

/** A vendor text field with the org's vendor names as suggestions; choosing one keeps its exact spelling. */
export function VendorInput({ value, onChange, testId }) {
  const names = useVendorNames();
  const listId = `nerc-vendors-${useId()}`;
  return (
    <>
      <input className={inputCls} value={value} onChange={(e) => onChange(e.target.value)} maxLength={200} list={listId}
        aria-label="Vendor" placeholder="Vendor" data-testid={testId} autoComplete="off" />
      <datalist id={listId} data-testid={testId ? `${testId}-suggestions` : undefined}>{names.map((n) => <option key={n} value={n} />)}</datalist>
    </>
  );
}

// ── Evaluation (P3) with Defender's new CVEs (P10) ───────────────────────────────────────────

const NO_CVE_REASON = {
  NoCveData: "Defender vulnerability data is not available for this organization.",
  NoLinkedDevices: "None of this source's in-scope assets is linked to a Defender device.",
};

function DefenderCves({ sourceId, onAdd }) {
  const apiCall = useApi();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    apiCall("GET", `/nerc-patches/sources/${sourceId}/defender-cves`)
      .then((res) => { if (live) setData(res); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [apiCall, sourceId]);

  if (failed) return <p className="text-xs text-gray-500">Could not read Defender&apos;s vulnerabilities.</p>;
  if (!data) return <p className="text-xs text-gray-500">Reading Defender…</p>;
  if (!data.available) return <p className="text-xs text-gray-500" data-testid="nerc-defender-none">{NO_CVE_REASON[data.reason] || "No Defender data."}</p>;
  return (
    <div data-testid="nerc-defender-cves">
      <p className="text-xs text-gray-600 mb-1">
        Defender reports {data.items.length}{data.truncated ? "+" : ""} vulnerabilit{data.items.length === 1 ? "y" : "ies"} on linked assets
        {data.since ? ` first seen since the last evaluation (${formatDateOnly(data.since)})` : ""}. An input only: record the vendor patches you evaluated.
      </p>
      {data.items.length > 0 && (
        <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
          <table className="w-full text-xs">
            <tbody>
              {data.items.map((c) => (
                <tr key={`${c.cyberAssetId}-${c.cveId}`} className="border-t border-gray-100 first:border-t-0">
                  <td className="px-2 py-1 font-mono">{c.cveId}</td>
                  <td className="px-2 py-1">{c.assetName}</td>
                  <td className="px-2 py-1 text-gray-500">{[c.softwareName, c.softwareVersion].filter(Boolean).join(" ")}</td>
                  <td className="px-2 py-1">{c.severity}</td>
                  <td className="px-2 py-1 text-right">
                    <button type="button" className={btn("secondary", "xs")} onClick={() => onAdd(c)} data-testid={`nerc-defender-add-${c.cveId}`}>Add as patch</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function EvaluationForm({ source, today, onClose, onSaved }) {
  const apiCall = useApi();
  const day = today || todayIso();
  const [outcome, setOutcome] = useState("NoNewPatches");
  const [completedOn, setCompletedOn] = useState(day);
  const [startedOn, setStartedOn] = useState("");
  const [evidence, setEvidence] = useState("");
  const [notes, setNotes] = useState("");
  const [rows, setRows] = useState([]);
  const [recorded, setRecorded] = useState(null);
  const result = useRef(null);
  const { saving, errors, submit } = useSubmit(() => {
    forgetVendorNames();
    const said = (result.current?.patches || []).filter((p) => p.note);
    if (said.length) setRecorded(said); else onSaved();
  });
  const setRow = (i, k, v) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const addRow = (row) => { setRows((rs) => [...rs, row]); setOutcome("PatchesFound"); };
  const withPatches = outcome === "PatchesFound";

  const onSubmit = (e) => {
    e.preventDefault();
    const local = withPatches ? patchRowProblems(rows) : [];
    if (completedOn > day) local.unshift("The evaluation date cannot be in the future.");
    if (startedOn && startedOn > completedOn) local.unshift("The start date cannot be after the date the evaluation was completed.");
    if (!evidence.trim()) local.unshift("Enter the evidence reference (advisory list, bulletin id or ticket).");
    if (withPatches && rows.length === 0) local.push("Record the patches found, or choose \"No new patches\".");
    submit(async () => {
      result.current = await apiCall("POST", `/nerc-patches/sources/${source.id}/evaluations`, {
        outcome, completedOn, startedOn: startedOn || null, evidenceReference: evidence, notes,
        patches: withPatches ? rows.map(rowToRequest) : [],
      });
    }, local);
  };

  if (recorded) {
    return (
      <Modal isOpen onClose={onSaved} title={`Evaluation of ${source.name} recorded`}>
        <div className="space-y-3" data-testid="nerc-patch-evaluation-notes">
          <p className="text-sm">Some patches were already recorded for this organisation; this evaluation was added to them:</p>
          <ul className="text-sm space-y-1 list-disc pl-5">{recorded.map((n) => <li key={n.patchId}><strong>{n.identifier}</strong>: {n.note}</li>)}</ul>
          <div className="flex justify-end"><button type="button" onClick={onSaved} className={btn("primary", "md")}>Done</button></div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen onClose={onClose} title={`Evaluate ${source.name}`} size="3xl">
      <form onSubmit={onSubmit} className="space-y-3" data-testid="nerc-patch-evaluation-form">
        <p className={noteCls}>
          Record the security patches released since the last evaluation (CIP-007 R2.2). Only &quot;Patches found&quot; and &quot;No new patches&quot; restart the 35-day clock.
          Each applicable patch must then be applied, or put under a mitigation plan, on every in-scope asset within 35 days.
          A patch already recorded (same vendor and identifier) is not duplicated: this evaluation is added to it.
        </p>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Outcome">
            <select className={inputCls} value={outcome} onChange={(e) => setOutcome(e.target.value)} data-testid="nerc-patch-outcome">
              {Object.entries(OUTCOME_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          <Field label="Started on (optional)"><input type="date" className={inputCls} value={startedOn} max={day} onChange={(e) => setStartedOn(e.target.value)} /></Field>
          <Field label="Completed on"><input type="date" className={inputCls} value={completedOn} max={day} onChange={(e) => setCompletedOn(e.target.value)} /></Field>
        </div>
        <Field label="Evidence reference" hint="The advisory list, bulletin id or ticket that shows what was reviewed.">
          <input className={inputCls} value={evidence} onChange={(e) => setEvidence(e.target.value)} maxLength={1000} data-testid="nerc-patch-evidence" />
        </Field>
        <Field label="Notes"><input className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} /></Field>
        {(outcome === "SourceUnavailable" || outcome === "Incomplete") && (
          <p className={warnCls} data-testid="nerc-patch-unsuccessful-note">This is recorded and shown, but it does not count as an evaluation: the 35-day clock keeps running.</p>
        )}
        {withPatches && (
          <>
            <DefenderCves sourceId={source.id} onAdd={(c) => setRows((rs) => (rs.some((r) => r.cves === c.cveId) ? rs : [...rs, rowFromCve(c, source.vendor)]))} />
            <div className="space-y-2">
              {rows.map((r, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-start border border-gray-200 rounded-lg p-2" data-testid={`nerc-patch-row-${i}`}>
                  <div className="col-span-3"><VendorInput value={r.vendor} onChange={(v) => setRow(i, "vendor", v)} testId={`nerc-patch-row-${i}-vendor`} /></div>
                  <input className={`${inputCls} col-span-3`} placeholder="Identifier (KB, advisory or version)" aria-label="Identifier" value={r.identifier} onChange={(e) => setRow(i, "identifier", e.target.value)} maxLength={200} />
                  <input className={`${inputCls} col-span-4`} placeholder="Title" aria-label="Title" value={r.title} onChange={(e) => setRow(i, "title", e.target.value)} maxLength={500} />
                  <button type="button" className={`col-span-2 justify-self-start mt-2 ${btn("danger", "xs")}`} onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}>Remove</button>
                  <input className={`${inputCls} col-span-3`} placeholder="Product (optional)" aria-label="Product" value={r.product} onChange={(e) => setRow(i, "product", e.target.value)} maxLength={200} />
                  <input className={`${inputCls} col-span-3`} placeholder="CVEs, comma separated (optional)" aria-label="CVEs" value={r.cves} onChange={(e) => setRow(i, "cves", e.target.value)} />
                  <input type="date" className={`${inputCls} col-span-3`} aria-label="Released on" value={r.releasedOn} max={day} onChange={(e) => setRow(i, "releasedOn", e.target.value)} />
                  <select className={`${inputCls} col-span-3`} aria-label="Applicability" value={r.applicability} onChange={(e) => setRow(i, "applicability", e.target.value)}>
                    <option value="Applicable">Applicable</option>
                    <option value="NotApplicable">Not applicable</option>
                  </select>
                  {r.applicability === "NotApplicable" && (
                    <input className={`${inputCls} col-span-12`} placeholder="Why it is not applicable" aria-label="Reason" value={r.reason} onChange={(e) => setRow(i, "reason", e.target.value)} maxLength={1000} />
                  )}
                </div>
              ))}
              <p className="text-[11px] text-gray-500">A vendor without a KB or advisory number: use its release name or version as the identifier (e.g. &quot;FortiOS 7.2.8&quot;).</p>
              <button type="button" className={btn("secondary")} onClick={() => addRow(emptyPatchRow(source.vendor))} data-testid="nerc-patch-add-row">Add a patch</button>
            </div>
          </>
        )}
        {!withPatches && (
          <button type="button" className={btn("secondary")} onClick={() => addRow(emptyPatchRow(source.vendor))} data-testid="nerc-patch-add-row">Patches were found: add one</button>
        )}
        <Errors items={errors} />
        <Actions saving={saving} onCancel={onClose} label="Record evaluation" />
      </form>
    </Modal>
  );
}

// ── An asset's state and the patch's applicability (P4) ──────────────────────────────────────

export function AssetStateForm({ patch, asset, today, onClose, onSaved }) {
  const apiCall = useApi();
  const day = today || todayIso();
  const [state, setState] = useState(asset.state === "Pending" || asset.state === "Mitigated" ? "Applied" : asset.state);
  const [stateOn, setStateOn] = useState(day);
  const [reason, setReason] = useState(asset.reason || "");
  const [evidence, setEvidence] = useState(asset.evidenceReference || "");
  const { saving, errors, submit } = useSubmit(onSaved);
  const onSubmit = (e) => {
    e.preventDefault();
    const local = [];
    if (state === "NotApplicable" && !reason.trim()) local.push("Say why the patch does not apply to this asset.");
    if (state === "Applied" && !evidence.trim()) local.push("Enter the evidence reference for the installation (change ticket or report).");
    if (state !== "Pending" && stateOn > day) local.push("The date cannot be in the future.");
    submit(() => apiCall("PUT", `/nerc-patches/patches/${patch.id}/assets/${asset.cyberAssetId}`, {
      state, stateOn: state === "Pending" ? null : stateOn, reason: reason || null, evidenceReference: evidence || null,
    }), local);
  };
  return (
    <Modal isOpen onClose={onClose} title={`${patch.identifier} on ${asset.assetName}`}>
      <form onSubmit={onSubmit} className="space-y-3" data-testid="nerc-patch-state-form">
        <p className={noteCls}>Decide by {formatDateOnly(asset.decisionDueOn)} for this asset.</p>
        <Field label="State">
          <select className={inputCls} value={state} onChange={(e) => setState(e.target.value)}>
            <option value="Applied">Applied</option>
            <option value="NotApplicable">Not applicable to this asset</option>
            <option value="Pending">Pending (undo)</option>
          </select>
        </Field>
        {state !== "Pending" && (
          <Field label={state === "Applied" ? "Applied on" : "Decided on"}><input type="date" className={inputCls} value={stateOn} max={day} onChange={(e) => setStateOn(e.target.value)} /></Field>
        )}
        {state === "Applied" && (
          <Field label="Evidence reference" hint="The change ticket or report showing the installation.">
            <input className={inputCls} value={evidence} onChange={(e) => setEvidence(e.target.value)} maxLength={1000} data-testid="nerc-patch-state-evidence" />
          </Field>
        )}
        {state !== "Pending" && (
          <Field label={state === "NotApplicable" ? "Reason" : "Notes (optional)"}><textarea className={inputCls} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} /></Field>
        )}
        <Errors items={errors} />
        <Actions saving={saving} onCancel={onClose} />
      </form>
    </Modal>
  );
}

export function ApplicabilityForm({ patch, today, onClose, onSaved }) {
  const apiCall = useApi();
  const day = today || todayIso();
  const to = patch.applicability === "Applicable" ? "NotApplicable" : "Applicable";
  const [reason, setReason] = useState("");
  const [effectiveOn, setEffectiveOn] = useState(day);
  const { saving, errors, submit } = useSubmit(onSaved);
  const pending = (patch.assets || []).filter((a) => a.state === "Pending").length;
  const onSubmit = (e) => {
    e.preventDefault();
    const local = [];
    if (!reason.trim()) local.push("Say why the applicability changes.");
    if (effectiveOn > day) local.push("The effective date cannot be in the future.");
    submit(() => apiCall("PUT", `/nerc-patches/patches/${patch.id}/applicability`, { applicability: to, reason, effectiveOn }), local);
  };
  return (
    <Modal isOpen onClose={onClose} title={`${patch.identifier}: ${to === "NotApplicable" ? "not applicable" : "applicable"}`}>
      <form onSubmit={onSubmit} className="space-y-3" data-testid="nerc-patch-applicability-form">
        {to === "NotApplicable"
          ? <p className={warnCls}>{pending} pending asset{pending === 1 ? "" : "s"} will be marked not applicable on the effective date. Assets already applied, not applicable or under a plan keep their state, and a decision past its date stays recorded as late.</p>
          : <p className={noteCls}>Each covered in-scope asset gets a pending decision, dated from the latest evaluation that found this patch.</p>}
        <Field label="Effective on"><input type="date" className={inputCls} value={effectiveOn} max={day} onChange={(e) => setEffectiveOn(e.target.value)} /></Field>
        <Field label="Reason"><textarea className={inputCls} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} /></Field>
        <Errors items={errors} />
        <Actions saving={saving} onCancel={onClose} label="Change applicability" />
      </form>
    </Modal>
  );
}

/** A manager's correction of a patch's vendor (e.g. "Unknown vendor" on a patch recorded before vendors were kept). */
export function VendorForm({ patch, onClose, onSaved }) {
  const apiCall = useApi();
  const [vendor, setVendor] = useState(patch.vendor === "Unknown vendor" ? "" : patch.vendor);
  const { saving, errors, submit } = useSubmit(() => { forgetVendorNames(); onSaved(); });
  const onSubmit = (e) => {
    e.preventDefault();
    submit(() => apiCall("PUT", `/nerc-patches/patches/${patch.id}/vendor`, { vendor }), vendor.trim() ? [] : ["Enter the vendor."]);
  };
  return (
    <Modal isOpen onClose={onClose} title={`Vendor of ${patch.identifier}`}>
      <form onSubmit={onSubmit} className="space-y-3" data-testid="nerc-patch-vendor-form">
        <p className={noteCls}>A patch is identified by its vendor and identifier. If another patch already has the vendor you enter, mark this one not applicable as a duplicate instead.</p>
        <Field label="Vendor"><VendorInput value={vendor} onChange={setVendor} testId="nerc-patch-vendor-input" /></Field>
        <Errors items={errors} />
        <Actions saving={saving} onCancel={onClose} />
      </form>
    </Modal>
  );
}

// ── Mitigation plans (P5) ────────────────────────────────────────────────────────────────────

const pairKey = (o) => `${o.patchId}:${o.cyberAssetId}`;
const pairsOf = (picked) => picked.map((k) => { const [patchId, cyberAssetId] = k.split(":").map(Number); return { patchId, cyberAssetId }; });

function PairPicker({ options, picked, onToggle, legend = "Covers" }) {
  return (
    <fieldset>
      <legend className="block text-xs font-medium text-gray-700 mb-1">{legend}</legend>
      <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
        {options.map((o) => (
          <label key={pairKey(o)} className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={picked.includes(pairKey(o))} onChange={() => onToggle(pairKey(o))} /> {o.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** A new plan over pending patch × asset pairs (options: [{ patchId, cyberAssetId, label }]). */
export function PlanForm({ options, users, today, onClose, onSaved }) {
  const apiCall = useApi();
  const day = today || todayIso();
  const [form, setForm] = useState({ title: "", ownerUserId: null, actions: "", reasonNotApplied: "", timeframeOn: "", timeframeEvent: "", picked: options.map(pairKey) });
  const { saving, errors, submit } = useSubmit(onSaved);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (key) => set("picked")(form.picked.includes(key) ? form.picked.filter((k) => k !== key) : [...form.picked, key]);

  const onSubmit = (e) => {
    e.preventDefault();
    const local = [];
    if (!form.title.trim()) local.push("A plan title is required.");
    if (!form.actions.trim()) local.push("Describe the planned actions.");
    if (!form.reasonNotApplied.trim()) local.push("Say why the patch is not applied now.");
    if (!form.timeframeOn) local.push("Enter the date the plan will be complete.");
    else if (form.timeframeOn < day) local.push("The timeframe cannot be in the past.");
    if (form.picked.length === 0) local.push("Choose at least one patch and asset the plan covers.");
    submit(() => apiCall("POST", "/nerc-patches/plans", {
      title: form.title, ownerUserId: form.ownerUserId, actions: form.actions, reasonNotApplied: form.reasonNotApplied,
      timeframeOn: form.timeframeOn, timeframeEvent: form.timeframeEvent || null, states: pairsOf(form.picked),
    }), local);
  };

  return (
    <Modal isOpen onClose={onClose} title="New mitigation plan" size="2xl">
      <form onSubmit={onSubmit} className="space-y-3" data-testid="nerc-patch-plan-form">
        <p className={noteCls}>A dated plan instead of applying the patch (CIP-007 R2.3). It must be implemented by its timeframe; any later change to the planned actions or the date needs the CIP Senior Manager&apos;s or a delegate&apos;s approval (R2.4).</p>
        <Field label="Title"><input className={inputCls} value={form.title} onChange={(e) => set("title")(e.target.value)} maxLength={200} /></Field>
        <UserSelect users={users} value={form.ownerUserId} onChange={set("ownerUserId")} label="Owner" testId="nerc-patch-plan-owner" />
        <Field label="Why the patch is not applied now"><textarea className={inputCls} rows={2} value={form.reasonNotApplied} onChange={(e) => set("reasonNotApplied")(e.target.value)} maxLength={1000} /></Field>
        <Field label="Planned actions"><textarea className={inputCls} rows={3} value={form.actions} onChange={(e) => set("actions")(e.target.value)} maxLength={4000} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Complete by"><input type="date" className={inputCls} value={form.timeframeOn} min={day} onChange={(e) => set("timeframeOn")(e.target.value)} /></Field>
          <Field label="Tied to an event (optional)" hint="E.g. the next scheduled outage; the date is still the commitment.">
            <input className={inputCls} value={form.timeframeEvent} onChange={(e) => set("timeframeEvent")(e.target.value)} maxLength={200} />
          </Field>
        </div>
        <PairPicker options={options} picked={form.picked} onToggle={toggle} />
        <Errors items={errors} />
        <Actions saving={saving} onCancel={onClose} label="Create plan" />
      </form>
    </Modal>
  );
}

/** Pending pairs joining an open plan on or before their decision dates (R2.3 "revise an existing plan"); no approval. */
export function AddToPlanForm({ options, plans, onClose, onSaved }) {
  const apiCall = useApi();
  const [planId, setPlanId] = useState(plans[0]?.id ?? "");
  const [picked, setPicked] = useState(options.map(pairKey));
  const { saving, errors, submit } = useSubmit(onSaved);
  const toggle = (key) => setPicked((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]));
  const onSubmit = (e) => {
    e.preventDefault();
    const local = [];
    if (!planId) local.push("Choose the plan.");
    if (picked.length === 0) local.push("Choose at least one patch and asset.");
    submit(() => apiCall("POST", `/nerc-patches/plans/${planId}/pairs`, { states: pairsOf(picked) }), local);
  };
  return (
    <Modal isOpen onClose={onClose} title="Add to an existing plan" size="xl">
      <form onSubmit={onSubmit} className="space-y-3" data-testid="nerc-patch-add-to-plan-form">
        <p className={noteCls}>The plan&apos;s actions and date stay as they are. Only assets still within their 35-day decision date can be added this way.</p>
        <Field label="Plan">
          <select className={inputCls} value={planId} onChange={(e) => setPlanId(Number(e.target.value))} data-testid="nerc-patch-add-to-plan-select">
            {plans.map((p) => <option key={p.id} value={p.id}>{p.title} — complete by {formatDateOnly(p.timeframeOn)}</option>)}
          </select>
        </Field>
        <PairPicker options={options} picked={picked} onToggle={toggle} legend="Add" />
        <Errors items={errors} />
        <Actions saving={saving} onCancel={onClose} label="Add to plan" />
      </form>
    </Modal>
  );
}

/** Completed actions recorded against the plan; no approval (P5.3). */
export function ProgressForm({ plan, onClose, onSaved }) {
  const apiCall = useApi();
  const [completed, setCompleted] = useState("");
  const { saving, errors, submit } = useSubmit(onSaved);
  const onSubmit = (e) => {
    e.preventDefault();
    submit(() => apiCall("POST", `/nerc-patches/plans/${plan.id}/progress`, { completedActions: completed }),
      completed.trim() ? [] : ["Describe the actions completed."]);
  };
  return (
    <Modal isOpen onClose={onClose} title={`Progress — ${plan.title}`}>
      <form onSubmit={onSubmit} className="space-y-3" data-testid="nerc-patch-progress-form">
        <p className={noteCls}>The planned actions and the date stay as they are.</p>
        <Field label="Actions completed"><textarea className={inputCls} rows={3} value={completed} onChange={(e) => setCompleted(e.target.value)} maxLength={4000} /></Field>
        <Errors items={errors} />
        <Actions saving={saving} onCancel={onClose} label="Record progress" />
      </form>
    </Modal>
  );
}

/** A revision of the planned actions or the date (earlier or later): always waits for approval (P5.4). */
export function ReviseForm({ plan, today, onClose, onSaved }) {
  const apiCall = useApi();
  const day = today || todayIso();
  const current = plan.versions?.find((v) => v.approvalState === "NotRequired" || v.approvalState === "Approved");
  const [actions, setActions] = useState(current?.actions || "");
  const [timeframeOn, setTimeframeOn] = useState(plan.timeframeOn || "");
  const [timeframeEvent, setTimeframeEvent] = useState(current?.timeframeEvent || "");
  const { saving, errors, submit } = useSubmit(onSaved);
  const later = plan.timeframeOn && timeframeOn > plan.timeframeOn;
  const onSubmit = (e) => {
    e.preventDefault();
    const local = [];
    if (!actions.trim()) local.push("Describe the planned actions.");
    if (!timeframeOn) local.push("Enter the date the plan will be complete.");
    else if (timeframeOn < day) local.push("The timeframe cannot be in the past.");
    submit(() => apiCall("POST", `/nerc-patches/plans/${plan.id}/versions`, { actions, timeframeOn, timeframeEvent: timeframeEvent || null }), local);
  };
  return (
    <Modal isOpen onClose={onClose} title={`Revise ${plan.title}`} size="xl">
      <form onSubmit={onSubmit} className="space-y-3" data-testid="nerc-patch-revise-form">
        <Field label="Planned actions"><textarea className={inputCls} rows={3} value={actions} onChange={(e) => setActions(e.target.value)} maxLength={4000} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Complete by" hint={`In force today: ${formatDateOnly(plan.timeframeOn)}.`}>
            <input type="date" className={inputCls} value={timeframeOn} min={day} onChange={(e) => setTimeframeOn(e.target.value)} />
          </Field>
          <Field label="Tied to an event (optional)"><input className={inputCls} value={timeframeEvent} onChange={(e) => setTimeframeEvent(e.target.value)} maxLength={200} /></Field>
        </div>
        <p className={warnCls} data-testid="nerc-patch-extension-note">
          {later ? "A later date is an extension. " : ""}A revision of the planned actions or the date takes effect only when the CIP Senior Manager or a delegate approves it.
          Until then the plan in force ({formatDateOnly(plan.timeframeOn)}) stays. To record work done without changing the plan, record progress instead.
        </p>
        <Errors items={errors} />
        <Actions saving={saving} onCancel={onClose} label={later ? "Request extension" : "Request revision"} />
      </form>
    </Modal>
  );
}

export function DecideForm({ plan, version, onClose, onSaved }) {
  const apiCall = useApi();
  const [note, setNote] = useState("");
  const { saving, errors, submit } = useSubmit(onSaved);
  const decide = (approve) => submit(() => apiCall("POST", `/nerc-patches/plans/${plan.id}/versions/${version.version}/decision`, { approve, note: note || null }),
    !approve && !note.trim() ? ["Give the reason for the rejection."] : []);
  const what = version.isExtension ? "Extension" : "Revision";
  return (
    <Modal isOpen onClose={onClose} title={`${what} of ${plan.title}`}>
      <div className="space-y-3" data-testid="nerc-patch-decide-form">
        <p className="text-sm">
          {version.timeframeOn !== plan.timeframeOn
            ? <>Move the timeframe from <strong>{formatDateOnly(plan.timeframeOn)}</strong> to <strong>{formatDateOnly(version.timeframeOn)}</strong>.</>
            : <>Keep the timeframe <strong>{formatDateOnly(plan.timeframeOn)}</strong>; change the planned actions.</>}
          {version.requestedByName && <span className="block text-xs text-gray-500">Requested by {version.requestedByName}</span>}
        </p>
        <p className="text-xs text-gray-600 whitespace-pre-wrap">{version.actions}</p>
        <Field label="Note (required to reject)"><textarea className={inputCls} rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} /></Field>
        <Errors items={errors} />
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={btn("neutral", "md")}>Cancel</button>
          <button type="button" disabled={saving} onClick={() => decide(false)} className={btn("danger", "md")}>Reject</button>
          <button type="button" disabled={saving} onClick={() => decide(true)} className={btn("primary", "md")}>Approve</button>
        </div>
      </div>
    </Modal>
  );
}

export function ImplementForm({ plan, today, onClose, onSaved }) {
  const apiCall = useApi();
  const day = today || todayIso();
  const [implementedOn, setImplementedOn] = useState(day);
  const [evidence, setEvidence] = useState("");
  const { saving, errors, submit } = useSubmit(onSaved);
  const onSubmit = (e) => {
    e.preventDefault();
    const local = [];
    if (implementedOn > day) local.push("The date cannot be in the future.");
    if (!evidence.trim()) local.push("Enter the evidence reference for the completed plan.");
    submit(() => apiCall("POST", `/nerc-patches/plans/${plan.id}/implemented`, { implementedOn, evidence }), local);
  };
  return (
    <Modal isOpen onClose={onClose} title={`Implemented — ${plan.title}`}>
      <form onSubmit={onSubmit} className="space-y-3" data-testid="nerc-patch-implement-form">
        <Field label="Implemented on"><input type="date" className={inputCls} value={implementedOn} max={day} onChange={(e) => setImplementedOn(e.target.value)} /></Field>
        <Field label="Evidence reference" hint="The report or ticket showing the plan's actions were completed.">
          <input className={inputCls} value={evidence} onChange={(e) => setEvidence(e.target.value)} maxLength={1000} data-testid="nerc-patch-implement-evidence" />
        </Field>
        <Errors items={errors} />
        <Actions saving={saving} onCancel={onClose} label="Mark implemented" />
      </form>
    </Modal>
  );
}
